/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Mon May 12 2025
 **/

// sender는 floatingListActions에서 처리하고, receiver는 stomp event 받으면 floatingListAction을 call해서 처리하도록 함
// stomp network에 이상이 있더라도 sender의 floating list는 정상작동 해야 하기 때문

import { useSetAtom } from "jotai";

import { EventHandler } from "@rfice/eventhandler";

import { useStompManager } from "@/frameworks/libraries";
import { Topics } from "@/frameworks/core/commons";
import { useUploadTus } from "@/frameworks/core/hooks";
import { useTusUploadTargetRenderer } from "@/frameworks/core/hooks";
import UnityApiController from "@/frameworks/core/unity/unityApiController";
import { listFileShare3DAtom } from "@/modules/filebox/components/Files/FileItem/private/FileCard";
import useWhiteboardStore from "@/modules/whiteboard/store/useWhiteboardStore";
import useFloatingListStore, { FileType, SharedItem } from "../store/useFloatingListStore";

type UserID = string | undefined;
type EventData = { tusID: string; percentage: number } | { tusID: string } | { senderId: UserID };

export default function useFloatingListActions() {
	const sharedList = useFloatingListStore((state) => state.sharedList);
	const addToSharedList = useFloatingListStore((state) => state.addToSharedList);
	const removeFromSharedList = useFloatingListStore((state) => state.removeFromSharedList);
	const updateLoadedFile = useFloatingListStore((state) => state.updateLoadedFile);
	const updateIsSharing = useFloatingListStore((state) => state.updateIsSharing);
	const clearSharedList = useFloatingListStore((state) => state.clearSharedList);
	const updateScreenLoading = useFloatingListStore((state) => state.updateScreenLoading);
	const updateIsExpanding = useFloatingListStore((state) => state.updateIsExpanding);

	const stompNetwork = useStompManager();
	const tusActions = useUploadTus();
	const tusUploadState = useTusUploadTargetRenderer("SPACE");

	const setListFileShare3D = useSetAtom(listFileShare3DAtom);

	/* Zone */
	const initFloatingList = (senderId: UserID) => {
		const currentZoneId = useWhiteboardStore.getState().currentZoneId;
		stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_request", { senderId });
	};

	const resetFloatingList = (senderId: UserID) => {
		// clear sharing screen
		removeItem(senderId, UnityApiController.instance.actorId);
		UnityApiController.instance.resetStreams();
		// clear uploading file
		Object.keys(tusUploadState).map((tusID) => {
			removeItem(senderId, tusID);
			tusActions.cancelUpload(tusID);
			tusActions.removeUploadedState(tusID);
		});
		// clear list
		clearSharedList();
	};

	/* Init Items */
	const initItems = {
		sendItems: (targetId: string, items: SharedItem[]) => {
			const currentZoneId = useWhiteboardStore.getState().currentZoneId;
			stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_init", { targetId, items });
		},
		init: (items: SharedItem[]) => {
			items.forEach((item) =>
				item.type === "screen" ? addToSharedList({ ...item, isLoading: true }) : addToSharedList(item)
			);

			// TODO :: refactor, merge into zustand
			const fileItems = items.filter((item) => item.type === "file") as FileType[];
			const newIds = fileItems.map((item) => item.fileInfo.id);
			setListFileShare3D((prev) => prev.concat(newIds));
		}
	};

	/* Add Remove */
	const addItem = (senderId: UserID, item: SharedItem) => {
		const currentZoneId = useWhiteboardStore.getState().currentZoneId;
		stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_add", { senderId, item });
		addToSharedList(item);
	};

	const removeItem = (senderId: UserID, fileId: string) => {
		const currentZoneId = useWhiteboardStore.getState().currentZoneId;
		stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_remove", { senderId, fileId });
		removeFromSharedList(fileId);
	};

	/* Upload File */
	const uploadFile = {
		// Start
		start: (senderId: UserID, item: FileType) => {
			addItem(senderId, item);
		},
		// Update
		updateProgress: (senderId: UserID, tusID: string, percentage: number) => {
			const currentZoneId = useWhiteboardStore.getState().currentZoneId;
			stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_progress", {
				senderId,
				tusID,
				percentage
			});
			EventHandler.fire("$FloatingListActions::UpdateProgress$", { tusID, percentage });
		},
		// Done
		done: (senderId: UserID, tusID: string, item: FileType) => {
			const currentZoneId = useWhiteboardStore.getState().currentZoneId;
			stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_done", { senderId, tusID, item });
			updateLoadedFile(tusID, item);
		},
		// Failed
		failed: (senderId: UserID, tusID: string) => {
			const currentZoneId = useWhiteboardStore.getState().currentZoneId;
			stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_failed", { senderId, tusID });
			EventHandler.fire("$FloatingListActions::FileUploadFailed$", { tusID });
		}
	};

	/* Close Screen */
	const requestRemoveScreenItem = (senderId: UserID, actorId: string) => {
		const currentZoneId = useWhiteboardStore.getState().currentZoneId;
		stompNetwork.publish(Topics.ToWhiteboard(currentZoneId), "floating_list_remove_screen", { senderId, actorId });
	};

	/* Fire Events */
	const fireEvent = (eventName: string, data: EventData) => {
		EventHandler.fire(`$FloatingListActions::${eventName}$`, data);
	};

	return {
		sharedList,
		initFloatingList,
		resetFloatingList,
		addItem,
		removeItem,
		addToSharedList,
		removeFromSharedList,
		updateLoadedFile,
		updateIsSharing,
		clearSharedList,
		uploadFile,
		initItems,
		fireEvent,
		requestRemoveScreenItem,
		updateScreenLoading,
		updateIsExpanding,
		getSharingStatus: (id: string) => sharedList.find((item) => item.id === id)?.isSharing,
		getExpandingStatus: (id: string) => sharedList.find((item) => item.id === id)?.isExpanding
	};
}
